/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/AddParticipatingCompany/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useRef, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import Select from "react-select";
import { Backdrop, CircularProgress } from "@mui/material";

import styles from "./index.module.css";

import { getBuyerCompanies } from "../../logic/buyerCompany";
import { getCustomerCompanyUsersByCompanyId } from "../../logic/customerCompanyUser";
import { getParticipatingCompanyAdminDetail } from "../../logic/businessEvents";
import ConfirmationModal from "../ConfirmationModal";

const AddParticipatingCompany = ({
  open,
  selectedParticipant,
  eventId,
  eventData,
  participatingCompanies,
  error,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const dialogRef = useRef(null);
  const [buyerCompanies, setBuyerCompanies] = useState([]);
  const [customerUsers, setCustomerUsers] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [customerUserRemoved, setCustomerUserRemoved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  // Seeded from `open` so the spinner is on screen in the same frame the
  // dialog appears, rather than one frame later when the fetch effect runs.
  const [isLoading, setIsLoading] = useState(open);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const deletionDialogRef = useRef(null);

  const [formData, setFormData] = useState({
    idEvento: eventId,
    empresa: null,
    tiempoBreak: "",
    ejecutivosCompradores: [],
  });

  // Generate break time options based on eventData
  const breakTimeOptions = useMemo(() => {
    if (
      !eventData ||
      !eventData.horaInicioBreak ||
      !eventData.horaFinBreak ||
      !eventData.duracionBreak
    ) {
      return [];
    }

    try {
      // Parse start time (format: "12:00" or "HH:MM")
      const startParts = eventData.horaInicioBreak.split(":");
      const startHour = parseInt(startParts[0], 10);
      const startMinute = parseInt(startParts[1], 10);

      // Parse end time (format: "13:00" or "HH:MM")
      const endParts = eventData.horaFinBreak.split(":");
      const endHour = parseInt(endParts[0], 10);
      const endMinute = parseInt(endParts[1], 10);

      // Parse duration (format: "0:20" or "00:20" -> hours and minutes)
      const durationParts = eventData.duracionBreak.split(":");
      const durationHours = parseInt(durationParts[0], 10);
      const durationMinutes = parseInt(durationParts[1], 10);
      const durationTotalMinutes = durationHours * 60 + durationMinutes;

      if (durationTotalMinutes <= 0) {
        return [];
      }

      const options = [];
      let currentHour = startHour;
      let currentMinute = startMinute;

      // Helper function to format time as HH:MM
      const formatTime = (hour, minute) => {
        return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      };

      // Generate time slots until we reach or exceed the end time
      while (
        currentHour < endHour ||
        (currentHour === endHour && currentMinute < endMinute)
      ) {
        // Calculate end time for this slot
        let slotEndHour = currentHour;
        let slotEndMinute = currentMinute + durationTotalMinutes;

        // Handle minute overflow
        while (slotEndMinute >= 60) {
          slotEndMinute -= 60;
          slotEndHour += 1;
        }

        // Check if this slot would exceed the break end time
        if (
          slotEndHour > endHour ||
          (slotEndHour === endHour && slotEndMinute > endMinute)
        ) {
          // If the slot would exceed, use the break end time as the slot end
          slotEndHour = endHour;
          slotEndMinute = endMinute;
        }

        const startTimeStr = formatTime(currentHour, currentMinute);
        const endTimeStr = formatTime(slotEndHour, slotEndMinute);
        const label = `${startTimeStr} - ${endTimeStr}`;
        const value = `${startTimeStr}-${endTimeStr}`;

        options.push({
          value: value,
          label: label,
        });

        // Move to next slot start time
        currentMinute += durationTotalMinutes;
        while (currentMinute >= 60) {
          currentMinute -= 60;
          currentHour += 1;
        }

        // If we've reached the end time, break
        if (
          currentHour > endHour ||
          (currentHour === endHour && currentMinute >= endMinute)
        ) {
          break;
        }
      }

      return options;
    } catch (error) {
      logger.error("Error generating break time options:", {
        err: sanitizeError(error),
      });
      return [];
    }
  }, [eventData]);

  // Effect for dialog management only
  useEffect(() => {
    const handleClose = () => {
      onCancel();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      // Check if dialog is already open before trying to open it
      if (dialog && !dialog.open) {
        dialog.showModal();
      }
    } else {
      // Only close if dialog is currently open
      if (dialog && dialog.open) {
        dialog.close();
      }
      // Reset state when modal closes
      setIsInitialized(false);
      setIsLoading(false);
      setIsEditMode(false);
      setCustomerUsers(null);
      setFormData({
        idEvento: eventId,
        empresa: null,
        tiempoBreak: "",
        ejecutivosCompradores: [],
      });
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onCancel, eventId]);

  // Effect for data fetching - only runs when modal opens and hasn't been initialized
  useEffect(() => {
    const fetchBuyerCompanies = async () => {
      try {
        // Every request only needs ids the caller already handed us, so they
        // all run at once and the spinner lasts as long as the slowest one.
        const [allBuyerCompanies, participantDetail, participantUsers] =
          await Promise.all([
            getBuyerCompanies(navigate),
            selectedParticipant
              ? getParticipatingCompanyAdminDetail(
                  selectedParticipant.idEvento,
                  selectedParticipant.idEmpresaCompradora,
                  navigate,
                )
              : null,
            selectedParticipant
              ? getCustomerCompanyUsersByCompanyId(
                  selectedParticipant.idEmpresaCompradora,
                  navigate,
                )
              : null,
          ]);

        if (selectedParticipant) {
          if (!participantDetail) {
            logger.error("Failed to load participating company for edit");
            onCancel();
            return;
          }

          setIsEditMode(true);
          setCustomerUsers(participantUsers);

          const currentCompany = allBuyerCompanies.find(
            (comp) => comp._id === selectedParticipant.idEmpresaCompradora,
          );
          setFormData({
            ...participantDetail,
            idEvento: participantDetail.idEvento,
            empresa: currentCompany,
          });
        }

        // Filter out companies already participating in the event
        const availableCompanies = allBuyerCompanies.filter(
          (company) =>
            !participatingCompanies.some(
              (participant) => participant.idEmpresaCompradora === company._id,
            ),
        );
        setBuyerCompanies(availableCompanies);
        setIsInitialized(true);
      } catch (error) {
        logger.error("Error fetching buyer companies:", {
          err: sanitizeError(error),
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (open && !isInitialized) {
      setIsLoading(true);
      fetchBuyerCompanies();
    }
  }, [open, isInitialized, selectedParticipant, navigate, eventId]);

  const checkSaveDisabled = () => {
    return (
      !formData.empresa ||
      !formData.tiempoBreak ||
      formData.ejecutivosCompradores.length === 0
    );
  };

  const handleCheckboxChange = (value) => {
    let newUsers;
    if (formData.ejecutivosCompradores.includes(value)) {
      setCustomerUserRemoved(true);
      newUsers = formData.ejecutivosCompradores.filter(
        (user) => user !== value,
      );
    } else {
      newUsers = [...formData.ejecutivosCompradores, value];
    }

    logger.info("newUsers: ", { data: newUsers });

    setFormData({ ...formData, ejecutivosCompradores: newUsers });
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
    setIsSubmitting(false);
  };

  const handleConfirm = async () => {
    if (isSubmitting) {
      return;
    } // Prevent multiple submissions

    try {
      setIsSubmitting(true);
      await onSuccess(formData, isEditMode);
    } catch (error) {
      logger.error("Error deleting participant:", {
        err: sanitizeError(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = () => {
    if (isEditMode && customerUserRemoved) {
      // Open confirmation modal
      setConfirmationOpen(true);
    } else {
      onSuccess(formData, isEditMode);
    }
  };

  return (
    <>
      {confirmationOpen && (
        <ConfirmationModal
          dialogRef={deletionDialogRef}
          open={confirmationOpen}
          onClose={handleCloseConfirmation}
          onConfirm={handleConfirm}
          title={t("components.confirmationModal.title")}
          message={t("components.addParticipantModal.deleteUserWarning")}
          confirmButtonText={t("components.confirmationModal.confirm")}
          cancelButtonText={t("components.confirmationModal.cancel")}
          isLoading={isSubmitting}
        />
      )}

      <dialog ref={dialogRef} className={styles.modal} aria-busy={isLoading}>
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          }}
          open={isLoading}
        >
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: "#4eabe7",
            }}
          />
        </Backdrop>

        <div className={styles.closeButtonContainer}>
          <button className={styles.closeButton} onClick={onCancel}>
            &times;
          </button>
        </div>

        <div className={styles.modalContent}>
          <label className={styles.modalTitle}>
            {t("components.addParticipantModal.title")}
          </label>
          <div className={styles.textContainer}>
            <label className={styles.modalSubtitle}>
              {t("components.addParticipantModal.description")}
            </label>
          </div>

          <div className={styles.inputGroup}>
            <div className={styles.inputCol}>
              <label className={styles.inputLabel}>
                {t("components.addParticipantModal.company")}
              </label>
              <Select
                options={buyerCompanies}
                isDisabled={isEditMode}
                isSearchable={false}
                value={formData.empresa}
                onChange={async (selectedOption) => {
                  if (selectedOption) {
                    logger.info("selected company: ", { data: selectedOption });
                    const customerUsers =
                      await getCustomerCompanyUsersByCompanyId(
                        selectedOption._id,
                        navigate,
                      );
                    setCustomerUsers(customerUsers);
                    setFormData({ ...formData, empresa: selectedOption });
                    logger.info("customerUsers", { data: customerUsers });
                  }
                }}
                placeholder={"Selecciona una empresa"}
                maxMenuHeight={140}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "8px",
                    width: "100%",
                    height: "40px",
                    fontSize: "16px",
                    cursor: "pointer",
                    textAlign: "left",
                    borderColor: "#909092",
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                    width: "100%",
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
                getOptionValue={(option) => option._id}
                formatOptionLabel={(option) => (
                  <span>{option.nombreComercial}</span>
                )}
              />
            </div>
            <div className={styles.inputCol}>
              <label className={styles.inputLabel}>
                {t("components.addParticipantModal.breakTime")}
              </label>
              <Select
                name="tiempoBreak"
                options={breakTimeOptions}
                isSearchable={false}
                value={breakTimeOptions.find(
                  (option) => option.value === formData.tiempoBreak,
                )}
                onChange={(selectedOption) => {
                  if (selectedOption) {
                    logger.info("selected break time: ", {
                      data: selectedOption,
                    });
                    setFormData({
                      ...formData,
                      tiempoBreak: selectedOption.value,
                    });
                  }
                }}
                placeholder={"11:30 AM - 12:30 PM"}
                maxMenuHeight={140}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "8px",
                    width: "100%",
                    height: "40px",
                    fontSize: "16px",
                    cursor: "pointer",
                    textAlign: "left",
                    borderColor: "#909092",
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                    width: "100%",
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
                getOptionValue={(option) => option.value}
                formatOptionLabel={(option) => <span>{option.label}</span>}
              />
            </div>
          </div>
          <div className={styles.customerUsersSection}>
            <label className={styles.sectionLabel}>
              {t("components.addParticipantModal.CustomerCompanyUsers")}
            </label>
            <div className={styles.customerUsersList}>
              {customerUsers && customerUsers.length > 0 ? (
                customerUsers.map((user) => (
                  <div key={user._id} className={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      id={user._id}
                      className={styles.checkbox}
                      checked={formData.ejecutivosCompradores.includes(
                        user._id,
                      )}
                      onChange={() => handleCheckboxChange(user._id)}
                    />
                    <label htmlFor={user._id} className={styles.checkboxLabel}>
                      {user.nombre}
                    </label>
                  </div>
                ))
              ) : (
                <span>{t("components.addParticipantModal.noUsers")}</span>
              )}
            </div>
            <span className={styles.errorMessage}>
              {error && t(`error.addParticipantModal.${error}`)}
            </span>
          </div>
        </div>

        <div className={styles.modalButtons}>
          <button onClick={onCancel} className={styles.cancelButton}>
            {t("components.addParticipantModal.cancelButton")}
          </button>
          <button
            className={styles.confirmButton}
            disabled={checkSaveDisabled()}
            onClick={() => onSubmit()}
          >
            {t("components.addParticipantModal.addButton")}
          </button>
        </div>
      </dialog>
      {/* {open && ()} */}
    </>
  );
};

export default AddParticipatingCompany;
